/**
 * Loading Milo's 3D head — the part that is about the browser rather than about a
 * component.
 *
 * # Why the renderer is not in the bundle
 *
 * The head is an owner-supplied package (`public/milo/<version>/`): a web component over
 * a pinned `@google/model-viewer` 4.3.1, about 1.8 MB of renderer, model and poster. It
 * is served as plain files and pulled in with a dynamic `import()` of a URL built at run
 * time, so Vite never sees it: nothing of it lands in a chunk, nothing of it is
 * downloaded by an account that has no assistant, and upgrading it is copying a folder
 * rather than rebuilding the portal. The price is that TypeScript cannot read its types
 * from the import either — they are copied into `types/Milo.ts`.
 *
 * # Why the queue is here and not in the component
 *
 * Two heads are on screen at once (the top bar and the drawer's header) and both mount
 * in the same tick. `milo-assistant.js` registers custom elements — `<model-viewer>` and
 * `<milo-assistant>` — and a second registration of a name that is already taken throws.
 * The package's own adapter therefore serialises loading in a module-level promise
 * chain, and it has to be ONE chain for the whole application: a per-component queue
 * would be no queue at all. A module-level `let` in a lib is exactly that, and it is
 * also why this is not a store — nothing here belongs to the account or survives a
 * reload.
 */
import type { MiloModule } from '@/types/Milo'

/**
 * The folder the head is served from.
 *
 * Versioned, and deliberately so. Caddy answers a real file under `/opt/dist` without a
 * `Cache-Control` header (deviation 13 puts the no-store headers on the SPA fallback,
 * which is right), so a browser is free to keep `milo-element.js` and `MILO-Head.glb`
 * for as long as it likes. Six files that must agree with each other and are cached
 * independently are six chances to run a new renderer against an old model. A new
 * version is a new folder and a one-line change here, so the mix cannot happen.
 */
export const MILO_ASSET_BASE = `${import.meta.env.BASE_URL}milo/1.1.0`

/** Serialises SDK loads across every head on the page. See the note above. */
let sdkQueue: Promise<void> = Promise.resolve()

/**
 * Imports the SDK and registers the custom elements, at most once at a time.
 *
 * `current()` is asked twice — before the import and after it — because a component can
 * be unmounted, or have its asset base changed, while the megabyte is on the wire. The
 * browser cannot cancel a native import; what it can do is refuse to act on one that
 * nobody is waiting for any more.
 */
export const loadMiloSdk = (url: string, current: () => boolean): Promise<void> => {
    const loading = sdkQueue.then(async () => {
        if (!current()) return

        const sdk = await import(/* @vite-ignore */ url) as MiloModule

        if (current()) await sdk.loadMiloAssistant()
    })

    // A failed load must not poison the chain for the next head that tries.
    sdkQueue = loading.catch(() => undefined)

    return loading
}

/** Asked once per page; the answer cannot change while the tab is open. */
let webglSupported: boolean | undefined

/**
 * Whether this browser can draw the head at all.
 *
 * Asked BEFORE the import rather than after it, so a machine without WebGL — a locked
 * down terminal, a virtual desktop with no GPU passthrough, a browser with the flag off
 * — draws the CSS orb instantly instead of downloading 1.8 MB in order to fail. The
 * canvas is thrown away immediately; the question is only whether a context exists.
 */
export const miloRendererSupported = (): boolean => {
    if (webglSupported !== undefined) return webglSupported

    try {
        const canvas = document.createElement('canvas')

        webglSupported = !!(canvas.getContext('webgl2') ?? canvas.getContext('webgl'))
    } catch {
        webglSupported = false
    }

    return webglSupported
}
